import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DashboardAbilities, DashboardData, DashboardWidgetType, WidgetConfig,
} from '@pm/shared';
import { api } from '@/lib/api';
import { homeBody, homeLists, keys, type Home } from '@/lib/query';

type Person = { id: string; name: string; avatarUrl: string | null };

export interface DashboardListItem {
  id: string;
  /** Null for an organization's own dashboard. */
  workspaceId: string | null;
  organizationId: string | null;
  folderId: string | null;
  name: string;
  isPrivate: boolean;
  createdById: string;
  createdBy: Person;
  updatedAt: string;
  /** What the person looking may do to it - decided by the API. */
  abilities: DashboardAbilities;
}

export interface DashboardWidget {
  id: string;
  type: DashboardWidgetType;
  title: string;
  config: WidgetConfig;
  /** Columns of the three-column grid it spans. */
  width: number;
  position: number;
}

export interface DashboardView extends DashboardListItem {
  /** Every connected board, including ones this person cannot open. */
  boardIds: string[];
  widgets: DashboardWidget[];
}

export interface BoardChoice {
  id: string;
  name: string;
  isPrivate: boolean;
  workspace: { id: string; name: string };
}

export const useDashboards = (workspaceId: string) =>
  useQuery({
    queryKey: keys.dashboards(workspaceId),
    queryFn: () => api.get<DashboardListItem[]>(`/workspaces/${workspaceId}/dashboards`),
    enabled: Boolean(workspaceId),
  });

export const useOrganizationDashboards = (organizationId: string) =>
  useQuery({
    queryKey: keys.organizationDashboards(organizationId),
    queryFn: () => api.get<DashboardListItem[]>(`/organizations/${organizationId}/dashboards`),
    enabled: Boolean(organizationId),
  });

export const useDashboard = (dashboardId: string) =>
  useQuery({
    queryKey: keys.dashboard(dashboardId),
    queryFn: () => api.get<DashboardView>(`/dashboards/${dashboardId}`),
    enabled: Boolean(dashboardId),
  });

/**
 * The widgets' numbers, for this person and these board chips. They are
 * never cached for long: the boards behind them change all day.
 */
export const useDashboardData = (dashboardId: string, boardIds: string[], enabled = true) =>
  useQuery({
    queryKey: keys.dashboardData(dashboardId, boardIds),
    queryFn: () => api.get<DashboardData>(
      `/dashboards/${dashboardId}/data${boardIds.length ? `?boardIds=${boardIds.join(',')}` : ''}`,
    ),
    enabled: Boolean(dashboardId) && enabled,
    staleTime: 0,
    // Switching chips keeps the last numbers on screen until the new ones land.
    placeholderData: keepPreviousData,
  });

/** Every board this person can open, in any workspace - what a dashboard may connect. */
export const useBoardChoices = (enabled: boolean) =>
  useQuery({
    queryKey: ['dashboards', 'board-choices'],
    queryFn: () => api.get<BoardChoice[]>('/dashboards/board-choices'),
    enabled,
  });

export const useDashboardMembers = (dashboardId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['dashboards', dashboardId, 'members'],
    queryFn: () => api.get<Person[]>(`/dashboards/${dashboardId}/members`),
    enabled: Boolean(dashboardId) && enabled,
  });

export function useCreateDashboard(home: Home) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; folderId: string | null; isPrivate: boolean }) =>
      api.post<DashboardView>('/dashboards', { ...body, ...homeBody(home) }),
    onSuccess: (dashboard) => {
      qc.setQueryData(keys.dashboard(dashboard.id), dashboard);
      void qc.invalidateQueries({ queryKey: homeLists(home).dashboards });
      void qc.invalidateQueries({ queryKey: homeLists(home).folders });
    },
  });
}

export function useDashboardActions(dashboardId: string, home: Home) {
  const qc = useQueryClient();
  // The workspace or organization page and the side panel list dashboards,
  // and a private one can decide whether a folder shows at all, so both refresh.
  const refreshLists = () => {
    void qc.invalidateQueries({ queryKey: homeLists(home).dashboards });
    void qc.invalidateQueries({ queryKey: homeLists(home).folders });
  };
  const keep = (dashboard: DashboardView) => {
    qc.setQueryData(keys.dashboard(dashboardId), dashboard);
    void qc.invalidateQueries({ queryKey: ['dashboards', dashboardId, 'data'] });
    refreshLists();
  };

  return {
    update: useMutation({
      mutationFn: (body: { name?: string; isPrivate?: boolean; folderId?: string | null; boardIds?: string[] }) =>
        api.put<DashboardView>(`/dashboards/${dashboardId}`, body),
      onSuccess: keep,
    }),
    remove: useMutation({
      mutationFn: () => api.delete(`/dashboards/${dashboardId}`),
      onSuccess: refreshLists,
    }),
    setMembers: useMutation({
      mutationFn: (userIds: string[]) => api.put<Person[]>(`/dashboards/${dashboardId}/members`, { userIds }),
      onSuccess: (members) => qc.setQueryData(['dashboards', dashboardId, 'members'], members),
    }),
  };
}

/** Adding, changing, removing and reordering widgets; the numbers reload after each. */
export function useWidgetActions(dashboardId: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.dashboard(dashboardId) });
    void qc.invalidateQueries({ queryKey: ['dashboards', dashboardId, 'data'] });
  };
  type Body = { type?: DashboardWidgetType; title: string; width: number; config: WidgetConfig };

  return {
    save: useMutation({
      mutationFn: ({ id, ...body }: Body & { id?: string }) => (id
        ? api.put<DashboardWidget>(`/dashboard-widgets/${id}`, { title: body.title, width: body.width, config: body.config })
        : api.post<DashboardWidget>(`/dashboards/${dashboardId}/widgets`, body)),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/dashboard-widgets/${id}`),
      onSuccess: refresh,
    }),
    reorder: useMutation({
      mutationFn: (widgetIds: string[]) => api.post(`/dashboards/${dashboardId}/widgets/reorder`, { widgetIds }),
      // Shown in the new order at once; a failure puts the server's back.
      onMutate: (widgetIds) => {
        qc.setQueryData<DashboardView>(keys.dashboard(dashboardId), (old) => old && {
          ...old,
          widgets: widgetIds.flatMap((id) => old.widgets.find((w) => w.id === id) ?? []),
        });
      },
      onError: refresh,
    }),
  };
}

/** Moving a dashboard between folders, from the workspace or organization page. */
export function useMoveDashboard(home: Home) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, folderId }: { dashboardId: string; folderId: string | null }) =>
      api.put(`/dashboards/${dashboardId}`, { folderId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeLists(home).dashboards });
      void qc.invalidateQueries({ queryKey: homeLists(home).folders });
    },
  });
}
